import { useSelector } from 'react-redux'
import {
  accessTypes,
  featureFlags,
  permissions,
  token,
  userId,
  userInfo,
} from './selectors'
import { PERMISSION_MODE } from './types'

export const useIsAuthenticated = () => !!useSelector(token)
export const useToken = () => useSelector(token)
export const useUserInfo = () => useSelector(userInfo)
export const useFeatureFlags = () => useSelector(featureFlags)
export const useUserAccessTypes = () => useSelector(accessTypes)
export const useUserId = () => useSelector(userId)
export const useUserType = () =>
  useSelector((state) => userInfo(state)?.user_type)
export const useUserPermissionMode = () =>
  useSelector((state) => userInfo(state)?.permission_mode)
export const useIsUser = (role) =>
  useSelector((state) => {
    if (typeof role === 'string') return userInfo(state)?.user_type === role
    else if (Array.isArray(role))
      return role.includes(userInfo(state)?.user_type)
    else if (role) throw new Error(`Invalid value ${role} for role`)
  })
export const useIsUserStaff = () =>
  useSelector((state) => !!userInfo(state)?.is_staff)
export const useIsUserSuperuser = () =>
  useSelector((state) => !!userInfo(state)?.is_superuser)
export const useHasPermission = (djangoModel, permissionName) =>
  useSelector(
    (state) =>
      djangoModel &&
      !!permissions(state)?.includes(
        `${permissionName}_${djangoModel.replace(/-/g, '')}`
      )
  )

/**
 * Unified capability check that bridges the legacy `user_type` hierarchy to
 * the new Django permission system while the two coexist.
 *
 * Call-sites that used to look like:
 *   const ok = useIsUser([USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER])
 *
 * should be rewritten as:
 *   const ok = useHasCapability('users.is_manager_level', {
 *     legacyRoles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER],
 *   })
 *
 * Behavior:
 *   - When the user is on `permission_mode === 'django'`, we check Django
 *     permissions via the `permissions` array on the auth slice (fed by the
 *     backend `UserInfo` endpoint). `capability` is an `app_label.codename`
 *     string, matching the format returned by `user.get_all_permissions()`.
 *     Superusers are not short-circuited here — the backend already returns
 *     every permission for them via `get_all_permissions()`.
 *   - Otherwise we fall back to `legacyRoles`, preserving the existing
 *     user_type-driven behavior.
 *
 * The two parameters let the same call-site keep working during the
 * migration without branching at every use.
 *
 * @param {string} capability `app_label.codename` (e.g. `users.is_manager_level`).
 * @param {{legacyRoles?: string[]|string}} [options]
 *   - `legacyRoles`: user_type(s) that satisfy the check while the user is
 *     still on legacy mode. If omitted, legacy users implicitly fail.
 * @returns {boolean}
 */
export const useHasCapability = (capability, options = {}) => {
  const { legacyRoles } = options
  return useSelector((state) => {
    const info = userInfo(state)
    if (!info) return false

    if (info.permission_mode === PERMISSION_MODE.DJANGO) {
      if (!capability) return false
      const userPerms = permissions(state)
      return !!userPerms?.includes(capability)
    }

    // Legacy `user_type` mode.
    if (!legacyRoles) return false
    if (typeof legacyRoles === 'string') return info.user_type === legacyRoles
    if (Array.isArray(legacyRoles)) return legacyRoles.includes(info.user_type)
    throw new Error(`Invalid value ${legacyRoles} for legacyRoles`)
  })
}
